import { useState } from 'react';
import { Plus, X, FileText, CheckSquare, FolderPlus, LogIn, LogOut, AlertCircle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import { derivePunchClockUiState } from '../utils/timeclockState';

interface MobileFABProps {
  onNewMatter: () => void;
  onNewNote: () => void;
  onNewTask: () => void;
}

export default function MobileFAB({ onNewMatter, onNewNote, onNewTask }: MobileFABProps) {
  const [expanded, setExpanded] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const queryClient = useQueryClient();

  const showFeedback = (type: 'success' | 'error', msg: string) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), type === 'error' ? 5000 : 3000);
  };

  // Check timeclock status. On 404 (no Kimai link) we still surface the
  // action so the user isn't left wondering why there's no clock button
  // (issue #254 — Brigit got stuck with no button and had to file a ticket).
  // bug #713: on transient 401/5xx, we ALSO keep the clock-in option visible
  // so the user can retry instead of seeing no button at all.
  const { data: clockStatus, error: clockError, isLoading: clockLoading } = useQuery({
    queryKey: ['timeclock-status'],
    queryFn: () => api.get('/timeclock/status').then(r => r.data),
    refetchInterval: 60_000,
    retry: false,
  });
  const punchUiState = derivePunchClockUiState(clockStatus, clockLoading, clockError);
  const clockUnlinked = punchUiState.kind === 'not-configured' && punchUiState.reason === 'unlinked';

  const punchIn = useMutation({
    mutationFn: () => api.post('/timeclock/punch-in').then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeclock-status'] });
      showFeedback('success', 'Punched in!');
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to punch in — please try again';
      showFeedback('error', detail);
    },
  });

  const punchOut = useMutation({
    mutationFn: () => api.post('/timeclock/punch-out').then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeclock-status'] });
      showFeedback('success', 'Punched out!');
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to punch out — please try again';
      showFeedback('error', detail);
    },
  });

  const isPunchedIn = punchUiState.kind === 'ready' && punchUiState.punchedIn;

  // Build dynamic action list
  const actions: { key: string; label: string; icon: typeof Plus; color: string }[] = [
    { key: 'matter', label: 'Matter', icon: FolderPlus, color: 'var(--primary)' },
    { key: 'task', label: 'Task', icon: CheckSquare, color: 'var(--success)' },
    { key: 'note', label: 'Note', icon: FileText, color: 'var(--info)' },
  ];

  if (punchUiState.kind === 'ready') {
    if (isPunchedIn) {
      actions.unshift({ key: 'clock-out', label: 'Clock Out', icon: LogOut, color: '#e53935' });
    } else {
      actions.unshift({ key: 'clock-in', label: 'Clock In', icon: LogIn, color: '#2e7d32' });
    }
  } else if (clockUnlinked) {
    actions.unshift({ key: 'clock-unlinked', label: 'Clock Setup', icon: AlertCircle, color: '#f59e0b' });
  } else if (punchUiState.kind === 'recoverable-error') {
    // Status query failed transiently — still expose Clock In so the user can
    // retry. The server validates "already punched in" on its own.
    actions.unshift({ key: 'clock-in', label: 'Clock In', icon: LogIn, color: '#2e7d32' });
  }

  const handleAction = (key: string) => {
    setExpanded(false);
    if (key === 'matter') onNewMatter();
    else if (key === 'note') onNewNote();
    else if (key === 'task') onNewTask();
    else if (key === 'clock-in') punchIn.mutate();
    else if (key === 'clock-out') punchOut.mutate();
    else if (key === 'clock-unlinked') {
      showFeedback('error', 'Time clock not set up yet — ask an admin to link your account.');
    }
  };

  return (
    <>
      {/* Backdrop */}
      {expanded && (
        <div
          onClick={() => setExpanded(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.3)',
            zIndex: 940,
          }}
        />
      )}

      {/* Mini FABs */}
      {expanded && actions.map((action, i) => {
        const Icon = action.icon;
        const bottomOffset = 16 + 52 + 12 + (i * 56); // gap + main FAB + spacing + stacked offset
        return (
          <div
            key={action.key}
            style={{
              position: 'fixed',
              bottom: `calc(var(--mobile-tab-bar-height, 56px) + var(--safe-bottom, env(safe-area-inset-bottom, 0px)) + ${bottomOffset}px)`,
              right: 16,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              zIndex: 951,
              animation: 'fabSlideUp 0.15s ease-out',
            }}
          >
            <span
              style={{
                background: 'var(--card)',
                padding: '0.3rem 0.6rem',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.78rem',
                fontWeight: 600,
                color: 'var(--text)',
                boxShadow: 'var(--shadow-md)',
                whiteSpace: 'nowrap',
              }}
            >
              {action.label}
            </span>
            <button
              onClick={() => handleAction(action.key)}
              aria-label={`New ${action.label}`}
              style={{
                width: 44,
                height: 44,
                minHeight: 44,
                borderRadius: '50%',
                background: 'var(--card)',
                border: `2px solid ${action.color}`,
                color: action.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: 'var(--shadow-md)',
                padding: 0,
              }}
            >
              <Icon size={20} />
            </button>
          </div>
        );
      })}

      {/* Main FAB */}
      <button
        onClick={() => setExpanded(!expanded)}
        aria-label={expanded ? 'Close menu' : 'Create new'}
        style={{
          position: 'fixed',
          bottom: 'calc(var(--mobile-tab-bar-height, 56px) + var(--safe-bottom, env(safe-area-inset-bottom, 0px)) + var(--mobile-action-gap, 16px))',
          right: 16,
          width: 'var(--mobile-fab-size, 52px)',
          height: 'var(--mobile-fab-size, 52px)',
          minHeight: 'var(--mobile-fab-size, 52px)',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #f43f5e, #e11d48)',
          color: '#fff',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(244, 63, 94, 0.4)',
          zIndex: 950,
          padding: 0,
          transition: 'transform 0.2s ease',
          transform: expanded ? 'rotate(45deg)' : 'rotate(0deg)',
        }}
      >
        {expanded ? <X size={24} /> : <Plus size={24} />}
      </button>

      {/* Punch feedback toast */}
      {feedback && (
        <div
          style={{
            position: 'fixed',
            bottom: 'calc(var(--mobile-tab-bar-height, 56px) + var(--safe-bottom, env(safe-area-inset-bottom, 0px)) + 80px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: feedback.type === 'success' ? '#2e7d32' : '#d32f2f',
            color: '#fff',
            padding: '0.6rem 1.2rem',
            borderRadius: 8,
            fontSize: '0.85rem',
            fontWeight: 600,
            zIndex: 960,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            whiteSpace: 'nowrap',
          }}
        >
          {feedback.msg}
        </div>
      )}

      <style>{`
        @keyframes fabSlideUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
